/* ============================================================
   Sherbet header — shared JS (mobile + search only).

   The desktop header was rebuilt and then removed; see
   docs/handoff-header-v3.md for the rationale. The pieces here
   survived because they work:

   - mobile push-sheet router  (.sh-sheet)
   - mobile tab bar hide-on-scroll  (.sh-tabbar[data-hide-on-scroll])
   - cart open event dispatch  ([data-cart-open] -> 'cart:open')
   - cart badge sync  (listens to 'cart:refreshed')
   - search overlay  (.sh-search dialog, Cmd/Ctrl+K, debounced predictive fetch)

   Mounted only when the corresponding snippet renders. The next
   header rebuild should keep these intact.
   ============================================================ */

(function () {
  'use strict';

  const SHEET_VIEW_ANIM = 280;
  const SEARCH_DEBOUNCE = 180;
  const SCROLL_THRESHOLD = 12;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  /* ----------------------------------------------------------
     Mobile tab bar hide-on-scroll-down (opt-in via data attr)
     ---------------------------------------------------------- */
  function initTabbarScroll() {
    const tabbar = $('[data-sh-tabbar]');
    if (!tabbar || tabbar.dataset.scrollBound === '1') return;
    if (tabbar.dataset.hideOnScroll !== 'true') return;
    tabbar.dataset.scrollBound = '1';

    let lastY = window.scrollY;
    let ticking = false;

    function update() {
      const y = window.scrollY;
      const delta = y - lastY;
      if (Math.abs(delta) < SCROLL_THRESHOLD) { ticking = false; return; }
      const hide = delta > 0 && y > 80;
      tabbar.classList.toggle('is-hidden', hide);
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ----------------------------------------------------------
     Mobile push-sheet (hamburger drawer router)
     ---------------------------------------------------------- */
  function initSheet() {
    const sheet = $('[data-sh-sheet]');
    if (!sheet || sheet.dataset.bound === '1') return;
    sheet.dataset.bound = '1';

    const views = $('[data-sheet-views]', sheet);
    const titleEl = $('[data-sheet-title]', sheet);
    const backBtn = $('[data-sheet-back]', sheet);
    const closeBtn = $('[data-sheet-close]', sheet);
    const openTriggers = $$('[data-mobile-sheet-open]');
    if (!views) return;

    const initialTitle = titleEl ? titleEl.textContent.trim() : '';
    const stack = [{ view: 'root', title: initialTitle }];
    let lastFocus = null;

    function showView(name) {
      $$('.sh-sheet__view', views).forEach(function (v) {
        const active = v.dataset.sheetView === name;
        v.classList.toggle('is-active', active);
        if (active) { v.hidden = false; }
      });
      setTimeout(function () {
        $$('.sh-sheet__view', views).forEach(function (v) {
          if (!v.classList.contains('is-active')) v.hidden = true;
        });
      }, SHEET_VIEW_ANIM);
    }

    function render() {
      const top = stack[stack.length - 1];
      showView(top.view);
      if (titleEl) titleEl.textContent = top.title;
      if (backBtn) backBtn.hidden = stack.length <= 1;
    }

    function openSheet() {
      lastFocus = document.activeElement;
      sheet.setAttribute('aria-hidden', 'false');
      sheet.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      stack.length = 1;
      stack[0] = { view: 'root', title: initialTitle };
      render();
      openTriggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      setTimeout(function () { if (closeBtn) closeBtn.focus(); }, 60);
    }

    function closeSheet() {
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
      document.body.style.overflow = '';
      openTriggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function pushView(name, title) {
      stack.push({ view: name, title: title });
      render();
    }

    function popView() {
      if (stack.length <= 1) return;
      stack.pop();
      render();
    }

    openTriggers.forEach(function (t) { t.addEventListener('click', openSheet); });
    if (closeBtn) closeBtn.addEventListener('click', closeSheet);
    if (backBtn) backBtn.addEventListener('click', popView);

    $$('[data-sheet-push]', sheet).forEach(function (btn) {
      btn.addEventListener('click', function () {
        pushView(btn.dataset.sheetPush, btn.dataset.sheetPushTitle || '');
      });
    });

    sheet.addEventListener('click', function (e) {
      if (e.target.closest('[data-sheet-close]') && !e.target.closest('.sh-sheet__panel')) {
        closeSheet();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.getAttribute('aria-hidden') === 'false') closeSheet();
    });

    // Focus trap: the sheet is a non-native dialog (<aside>), so keep Tab
    // cycling within the open panel instead of escaping to the page behind.
    sheet.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (sheet.getAttribute('aria-hidden') !== 'false') return;
      const focusables = $$(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        sheet
      ).filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----------------------------------------------------------
     Cart open dispatcher + cart badge sync
     ---------------------------------------------------------- */
  function initCartHooks() {
    if (document.documentElement.dataset.shHeaderCartBound === '1') return;
    document.documentElement.dataset.shHeaderCartBound = '1';

    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-cart-open]');
      if (!trigger) return;
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:open'));
    });

    document.addEventListener('cart:refreshed', function (e) {
      const cart = (e && e.detail && e.detail.cart) || null;
      if (!cart) return;
      const count = cart.item_count || 0;
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    });
  }

  /* ----------------------------------------------------------
     Search overlay (dialog + predictive fetch)
     ---------------------------------------------------------- */
  function initSearch() {
    const dialog = $('[data-sh-search]');
    if (!dialog || dialog.dataset.bound === '1') return;
    dialog.dataset.bound = '1';

    const input = $('[data-sh-search-input]', dialog);
    const results = $('[data-sh-search-results]', dialog);
    const defaultEl = $('[data-sh-search-default]', dialog);
    const closeBtn = $('[data-sh-search-close]', dialog);
    const loadingText = dialog.dataset.loadingText || 'Searching';

    let fetchController = null;
    let debounceTimer = null;

    // Idle (suggestions) vs. live results: only one is shown at a time.
    function showDefault() {
      if (defaultEl) defaultEl.hidden = false;
      if (results) { results.hidden = true; results.innerHTML = ''; }
    }
    function showResults() {
      if (defaultEl) defaultEl.hidden = true;
      if (results) results.hidden = false;
    }

    function open() {
      if (typeof dialog.showModal === 'function') {
        try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
      } else {
        dialog.setAttribute('open', '');
      }
      showDefault();
      setTimeout(function () { if (input) input.focus(); }, 60);
    }

    function close() {
      if (typeof dialog.close === 'function') {
        try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); }
      } else {
        dialog.removeAttribute('open');
      }
      if (input) input.value = '';
      if (fetchController) { fetchController.abort(); fetchController = null; }
      showDefault();
    }

    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-search-open]');
      if (!trigger) return;
      e.preventDefault();
      open();
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });

    document.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (dialog.hasAttribute('open')) close(); else open();
      }
      if (e.key === 'Escape' && dialog.hasAttribute('open')) close();
    });

    // Arrow-key roving through the visible suggestion / result links.
    // Up past the first item returns focus to the input.
    dialog.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const scope = results && !results.hidden ? results : defaultEl;
      if (!scope) return;
      const links = $$('a, button', scope);
      if (!links.length) return;
      e.preventDefault();
      const idx = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        (links[idx + 1] || links[0]).focus();
      } else if (idx <= 0) {
        if (input) input.focus();
      } else {
        links[idx - 1].focus();
      }
    });

    if (input) {
      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        const q = input.value.trim();
        if (!q) { showDefault(); return; }
        showResults();
        if (results) {
          results.innerHTML =
            '<div class="sh-search__status" role="status">' +
            '<span class="sh-search__spinner" aria-hidden="true"></span>' +
            '<span class="visually-hidden">' + loadingText + '</span></div>';
        }
        debounceTimer = setTimeout(function () { runSearch(q); }, SEARCH_DEBOUNCE);
      });
    }

    function runSearch(q) {
      if (fetchController) fetchController.abort();
      fetchController = new AbortController();
      const url = '/search/suggest?q=' + encodeURIComponent(q) +
                  '&resources[limit]=6&resources[type]=product,collection,page,article,query' +
                  '&section_id=predictive-search';
      fetch(url, { signal: fetchController.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (!results) return;
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const inner = tmp.querySelector('.predictive-search') || tmp.firstElementChild;
          results.innerHTML = '';
          if (inner) results.appendChild(inner);
        })
        .catch(function (e) {
          if (e.name !== 'AbortError' && results) {
            results.innerHTML = '';
          }
        });
    }
  }

  /* ----------------------------------------------------------
     Desktop mega menu (click-to-open, anchored, keyboard + ARIA)
     ---------------------------------------------------------- */
  function megaSetOpen(item, open) {
    item.classList.toggle('is-open', open);
    const trigger = $('[data-mega-trigger]', item);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function megaCloseAll(except) {
    $$('[data-mega-item].is-open').forEach(function (item) {
      if (item !== except) megaSetOpen(item, false);
    });
  }

  function initMegaMenu() {
    const nav = $('.sh-header__nav');
    const items = nav ? $$('[data-mega-item]', nav) : [];

    // Per-nav trigger binding (guarded so editor section reloads don't double-bind).
    if (nav && items.length && nav.dataset.megaBound !== '1') {
      nav.dataset.megaBound = '1';

      // Shared close-delay timer so moving the pointer from the trigger
      // across the gap into the panel doesn't snap the menu shut.
      let closeTimer = null;

      items.forEach(function (item) {
        const trigger = $('[data-mega-trigger]', item);
        const panel = $('[data-mega-panel]', item);
        if (!trigger || !panel) return;

        // Hover to open (mouse). The panel is a descendant of the item, so
        // entering it keeps the item "hovered"; the delay covers the gap.
        item.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
          megaCloseAll(item);
          megaSetOpen(item, true);
        });
        item.addEventListener('mouseleave', function () {
          clearTimeout(closeTimer);
          closeTimer = setTimeout(function () { megaSetOpen(item, false); }, 150);
        });

        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          const open = item.classList.contains('is-open');
          megaCloseAll(item);
          megaSetOpen(item, !open);
        });

        // ArrowDown from the trigger opens and moves into the panel.
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            megaCloseAll(item);
            megaSetOpen(item, true);
            const first = $('a, button', panel);
            if (first) first.focus();
          }
        });

        // Roving Up/Down through the panel's links; Up past the top returns to the trigger.
        panel.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          const focusables = $$('a, button', panel);
          if (!focusables.length) return;
          e.preventDefault();
          const idx = focusables.indexOf(document.activeElement);
          if (e.key === 'ArrowDown') {
            (focusables[idx + 1] || focusables[0]).focus();
          } else if (idx <= 0) {
            trigger.focus();
          } else {
            focusables[idx - 1].focus();
          }
        });
      });
    }

    // Global close handlers, bound once for the document lifetime.
    if (document.documentElement.dataset.shMegaGlobal !== '1') {
      document.documentElement.dataset.shMegaGlobal = '1';

      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-mega-item]')) megaCloseAll(null);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        const open = $('[data-mega-item].is-open');
        if (!open) return;
        e.preventDefault();
        megaSetOpen(open, false);
        const trigger = $('[data-mega-trigger]', open);
        if (trigger) trigger.focus();
      });

      // Close when focus leaves the open menu (e.g. tabbing out to the actions).
      document.addEventListener('focusin', function (e) {
        if (!e.target.closest('[data-mega-item]')) megaCloseAll(null);
      });
    }
  }

  function boot() {
    initTabbarScroll();
    initSheet();
    initCartHooks();
    initSearch();
    initMegaMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    boot();
  });
})();
